import isNil from 'lodash.isnil';
import get from 'lodash.get';
import head from 'lodash.head';
import isEmpty from 'lodash.isempty';
import flatMap from 'lodash.flatmap';
import compact from 'lodash.compact';
import { determineStudentGroup, getGroupsByGradeAndPeriod } from '@amplify/instruction-groups';
import { compositeScoresService } from '@amplify/dibels8-composite-score';
import { getStrugglingAdvancedGoalForMeasure } from '@amplify/struggling-advanced-goal';
import { lexileGradeAvailability } from '../../constants/reading/LexileGradeMapper';
import {
    FR_EXPRESS_VIEW_MEASURES,
    MEASURE,
    MEASURES,
    SUMMARY_MEASURES,
} from '../../constants/reading/MeasuresConstants';
import { getLicensedMeasures, getSpecificReadingGoal } from '../../utils/reading/filterUtils';
import { INTERP_COLOR_MAPPER } from '../../constants/shared/CommonInterpretationMapper';
import DO_INTERP_COLOR_MAPPER from '../../constants/reading/CommonInterpretationMapper';
import { LICENSE } from '../../constants/reading/CommonConstants';
import {
    getInstructionStatusesDataByStudent,
    getSharedIntercomInfo,
    getStudentGoal,
    getStudentInfo,
    getStudentStatusesById,
} from '../shared/rootHelpers';
import {
    getPmStatusesByPeriod,
    getStudentByMeasureYearPeriodGrade,
    getStudentResultByMeasureYearGradePeriod,
    getStudentResultsByMeasureYearGradePeriod,
    getStudentsByGrade,
    isStudentHasUnofficialResults,
    getGoalByMeasureGradePeriod,
    isDyslexiaRiskDisplayed,
} from '../../utils/shared/filterUtils';
import { getClassSummaryColumns } from '../../components/resultsTable/shared/resultsHelper';
import { getStudentColumns } from '../../components/resultsTable/reading/resultsHelper';
import { getStudentDataByMeasures } from '../../utils/reading/selectorUtils';
import { getGradeTitle, getSupportedGrade, GRADE } from '../../constants/shared/GradesConstants';
import { getPmPeriodByCurrentPeriod, INTERP, PERIOD } from '../../constants/shared/CommonConstants';
import {
    generateReadingStats,
    getSentencesWithMergedMarks,
    getSplitedEvents,
} from '../../utils/reading/troUtils';
import {
    generateObservations,
    getMatchedWithContentObservations,
} from '../../utils/shared/instructionUtils';
import { INSTRUCTION_GROUPS_TYPES } from '../../constants/shared/InstructionConstants';
import { getEndPmPeriod, getStartPmPeriod } from '../../utils/shared/pmUtils';
import { getInterventionForStudentAndPmPeriod, getPmGridData } from '../../utils/reading/pmUtils';
import { getD8MeasureByGrade } from '../../constants/reading/DefaultGradeToMeasureMapper';
import { getStudentOverallObject } from '../../utils/shared/growthAnalysisUtils';
import { getOverallPerformance } from '../../features/featureReport/shared/featureReportHelper';
import getAvailableMeasuresForView, {
    isShowOnSummaryViewByMeasure,
    isStudentAssessed,
    isStudentInProgress,
} from '../../features/featureReport/reading/featureReportHelper';
import getOverallPerformanceExpress from '../../features/featureReport/reading/featureReportExpressHelper';
import {
    FR_EXPRESS_VIEW,
    FR_READING_VIEW,
    FR_SUMMARY_VIEW_EXPRESS,
    MEASURE_TITLE_EXPRESS,
} from '../../features/featureReport/reading/FeatureReportConstants';
import {
    getMeasurePeriodCombinationForGrades,
    getSummaryMeasuresForGrades,
    getUniqueMeasuresForGrades,
} from '../../utils/shared/gradeToMeasureUtils';
import {
    getHeaderObjects,
    getResultsObjects,
} from '../../features/studentSummary/shared/benchmarkHistory/benchmarkHistoryHelper';
import { D8_MEASURES, TRC_OL_MEASURES } from '../../constants/shared/MeasuresConstants';
import { getD8ReportPeriod, getD8ReportSubtype } from '../../utils/reading/intercomUtils';
import { getSortedResultsByDate, sortByPredefinedOrder } from '../../utils/shared/sortUtils';
import { FR_SUMMARY_VIEW } from '../../features/featureReport/shared/FeatureReportConstants';
import PmThresholdsMapper from '../../features/instruction/reading/PmThresholdsMapper';
import {
    ALL_STUDENT_SUMMARY_PDF,
    ALL_PM_GRAPHS_PDF,
    TRO_CLASS_SUMMARY_PDF,
    DUAL_LANGUAGE_CLASS_SUMMARY_PDF,
    CURRENT_PAGE,
    CLASS_SUMMARY_PDF,
} from '../../constants/shared/PdfGenerationConstants';
import { getTitlePdf } from '../../utils/shared/headerUtils';
import { hasMeasureGradeNoExceptions } from '../../utils/shared/tableUtils';
import { getPdfAvailabilityMapper, isAvailableToyPdfByMeasure } from '../../utils/shared/pdfUtils';
import { getGoalReading } from '../../utils/reading/cellUtils';
import { isDualLanguageEnabled } from '../../utils/shared/dualLanguageUtils';
import isStandaloneExpress from '../../utils/reading/expressUtils';
import { ALL_MEASURES } from '../../constants/reading/HistoricalMeasuresAvailabilityByGrades';
import KeycloakClient from '../../api/clients/shared/KeycloakClient';

const { OVERALL, TRO, LEXILE, DO, MAZE, VOCABO, SPELLING, RAN } = MEASURE;
const { EXPRESS, BURST, CKLA_2_EDITION } = LICENSE;
const { PM, SUMMARY } = PERIOD;
const { ALL_GRADES } = GRADE;
const { TRO_EXPRESS, DO_EXPRESS } = MEASURE_TITLE_EXPRESS;
const { RED, YELLOW, GREEN, BLUE, BELOW, PROFICIENT } = INTERP;

const getTroBookItems = (bookData, currentBookResult) => ({
    listOfWords: get(bookData, 'words', []),
    startPosition: get(currentBookResult, 'vrrResult.startPosition'),
    endPosition: get(currentBookResult, 'vrrResult.endPosition'),
    modifiedEvents: getSplitedEvents(get(currentBookResult, 'vrrResult.itemLevelDetails', [])),
});

export const getBenchmarkData = ({
    studentsByGrade,
    results,
    interps,
    tableMeasures,
    year,
    selectedPeriod,
    currentPeriod,
    gradeToMeasureMapper,
    isLexileShown,
    isDTC = false,
}) => ({
    tableMeasures,
    studentsByGrade,
    interps,
    year,
    selectedPeriod,
    currentPeriod,
    rows: studentsByGrade.map(({ grade, students }) => ({
        grade,
        students: students.map(student => {
            const statuses = getStudentStatusesById(results, student.externalId);

            return {
                studentInfo: getStudentInfo(student),
                isDyslexiaRiskDisplayed: isDyslexiaRiskDisplayed(
                    statuses,
                    year,
                    selectedPeriod,
                    currentPeriod,
                ),
                isStudentHasUnofficialResults: isStudentHasUnofficialResults(
                    statuses,
                    year,
                    selectedPeriod,
                ),
                grade,
                columns: getStudentColumns(
                    tableMeasures,
                    selectedPeriod,
                    statuses,
                    year,
                    grade,
                    interps,
                    isLexileShown,
                    gradeToMeasureMapper,
                    isDTC,
                ),
            };
        }),
    })),
});

export const getStudentsDataByMeasuresForAllPMGraphs = (
    studentsWithStatuses,
    interps,
    licensingInfo,
    studentGoals,
    currentPeriod,
    currentSchoolYear,
    gradeToMeasureMapper,
    summerPeriodLabel,
) => {
    return studentsWithStatuses.map(student => {
        const { grade, studentInfo, statuses: studentStatuses } = student;
        const { firstName, lastName } = studentInfo;
        const { dataByMeasure } = getStudentDataByMeasures(
            studentStatuses,
            interps,
            grade,
            currentSchoolYear,
            licensingInfo,
            studentGoals,
            studentInfo.id,
            gradeToMeasureMapper,
            summerPeriodLabel,
        );

        return {
            student: {
                firstName,
                lastName,
            },
            gradeLevel: grade,
            period: currentPeriod,
            periodLabel: currentPeriod === PERIOD.SUMMER ? summerPeriodLabel : currentPeriod,
            dataByMeasure,
        };
    });
};

export const getGradeToMeasureMapperWithLexile = gradeToMeasureMapper => {
    return gradeToMeasureMapper.map(({ key, measures }) => {
        const measuresWithLexile = lexileGradeAvailability.includes(key)
            ? [LEXILE, ...measures]
            : measures;
        return {
            key,
            measures: measuresWithLexile,
        };
    });
};

export const getMeasureMapper = measure =>
    measure === DO ? DO_INTERP_COLOR_MAPPER : INTERP_COLOR_MAPPER;

const getMazeStudentsData = (results, students, year, period, pmPeriod, windowDates, toyPeriods) =>
    students.map(student => {
        const studentResults = get(results, `${student.externalId}.results`, []);
        const pmStatusesByPeriod = getPmStatusesByPeriod(
            getStudentResultsByMeasureYearGradePeriod(studentResults, MAZE, year, null, PM),
            pmPeriod,
            windowDates,
            toyPeriods,
        );
        return {
            studentInfo: getStudentInfo(student),
            bmResult: getStudentResultByMeasureYearGradePeriod(
                studentResults,
                MAZE,
                year,
                null,
                period,
            ),
            pmResult: head(getSortedResultsByDate(pmStatusesByPeriod)),
        };
    });

export const getChosenClassStudentsMazeResultsByGrade = (
    studentsByGrade,
    results,
    period,
    year,
    pmPeriod,
    windowDates,
    toyPeriods,
) => {
    const studentResults = [];
    studentsByGrade.forEach(studentsByGradeObject => {
        studentResults.push({
            grade: studentsByGradeObject.grade,
            gradeTitle: getGradeTitle(studentsByGradeObject.grade),
            studentsData: getMazeStudentsData(
                results,
                studentsByGradeObject.students,
                year,
                period,
                pmPeriod,
                windowDates,
                toyPeriods,
            ),
        });
    });
    return studentResults;
};

export const getTROSentences = (currentBookData, currentBookResult) => {
    const { listOfWords, modifiedEvents } = getTroBookItems(currentBookData, currentBookResult);

    return [...listOfWords, { word: '' }].map((word, index) => {
        const wordWithMarks = { ...word };

        modifiedEvents.forEach(mark => {
            const { position, startPosition, endPosition } = mark.eventDetails;
            if (index === position || (index >= startPosition && index <= endPosition)) {
                wordWithMarks.marks = !isEmpty(wordWithMarks.marks)
                    ? [...wordWithMarks.marks, mark]
                    : [mark];
            }
        });

        return wordWithMarks;
    });
};

export const getReadingStats = (currentBookData, currentBookResult) => {
    const { listOfWords, startPosition, endPosition, modifiedEvents } = getTroBookItems(
        currentBookData,
        currentBookResult,
    );
    const modifiedItemLevelDetails = getSentencesWithMergedMarks(modifiedEvents);
    return generateReadingStats(modifiedItemLevelDetails, listOfWords, startPosition, endPosition);
};

export const getInstructionStudentsDataForSelectedGrade = ({
    studentsByGrade,
    studentsResults,
    currentYear,
    checkPointPeriod,
    selectedGrade,
    selectedDate,
    windowDates,
    licensingInfo,
    currentPeriod,
    interventions,
    observationsContent,
    toyPeriods,
}) => {
    return getStudentsByGrade(studentsByGrade, selectedGrade).map(student => {
        const { bmStatuses, bestStatuses, isSecureFound } = getInstructionStatusesDataByStudent(
            studentsResults,
            student,
            currentYear,
            checkPointPeriod,
            selectedDate,
            windowDates,
            PmThresholdsMapper,
            toyPeriods,
        );

        const studentGroup = determineStudentGroup(
            selectedGrade,
            checkPointPeriod,
            bestStatuses,
            INSTRUCTION_GROUPS_TYPES.READING,
        );

        const observations = generateObservations(
            [].concat(
                ...bestStatuses.map(status =>
                    getMatchedWithContentObservations(status, observationsContent),
                ),
            ),
        );

        const isMasUser = KeycloakClient.isMasUser();
        let intervention = {};
        if (isMasUser) {
            intervention = licensingInfo.includes(BURST)
                ? getInterventionForStudentAndPmPeriod(
                      interventions,
                      student.externalId,
                      getPmPeriodByCurrentPeriod(currentPeriod),
                  )
                : {};
        } else {
            intervention = getInterventionForStudentAndPmPeriod(
                interventions,
                student.externalId,
                getPmPeriodByCurrentPeriod(currentPeriod),
            );
        }

        const applyTroActivities = get(
            bestStatuses.find(status => status.measure === TRO),
            'isComplete',
            false,
        );

        return {
            studentInfo: getStudentInfo(student),
            groupName: get(studentGroup, 'name'),
            bestStatuses,
            bmStatuses,
            additionalGroupData: intervention,
            additionalActivities: applyTroActivities ? get(studentGroup, 'activities', []) : [],
            observations,
            isSecureFound,
        };
    });
};

export const getInstructionGroupsForSelectedGrade = (
    selectedGrade,
    checkPointPeriod,
    studentsData,
    licensingInfo,
    observations,
) => {
    const groups = [];
    const allGroups = getGroupsByGradeAndPeriod(
        INSTRUCTION_GROUPS_TYPES.READING,
        selectedGrade,
        checkPointPeriod,
    );
    allGroups.forEach(group => {
        const studentsInGroup = studentsData.filter(student => student.groupName === group.name);
        if (!isEmpty(studentsInGroup)) {
            const studentGroup = {
                name: group.name,
                skill: group.skill,
                students: studentsInGroup.map(student => student.studentInfo),
                observations: generateObservations(
                    [].concat(
                        ...flatMap(studentsInGroup, ({ bestStatuses }) =>
                            bestStatuses
                                .filter(status =>
                                    get(group, 'subtests', []).includes(status.measure),
                                )
                                .map(status =>
                                    getMatchedWithContentObservations(status, observations),
                                ),
                        ),
                    ),
                ),
                text: group.text,
                activities: group.activities,
                widgetData: licensingInfo.includes(CKLA_2_EDITION) ? group.cklaLessons : [],
            };
            groups.push(studentGroup);
        }
    });

    return groups;
};

export const getStudentComputedGoals = (
    studentsByGrade,
    results,
    interps,
    customGoals,
    schoolYear,
    gradeToMeasureMapper,
    toyPeriods,
) =>
    studentsByGrade.reduce(
        (studentGoalsByGrade, { grade, students }) => ({
            ...studentGoalsByGrade,
            ...students.reduce((studentGoals, student) => {
                const { externalId: studentId } = student;
                const measureByGrade = getD8MeasureByGrade(grade, false, gradeToMeasureMapper);
                return {
                    ...studentGoals,
                    [studentId]: flatMap(measureByGrade, measure => {
                        const statuses = getStudentResultsByMeasureYearGradePeriod(
                            getStudentStatusesById(results, studentId),
                            measure,
                            schoolYear,
                            grade,
                        );
                        const interpsByMeasureGrade = getStudentResultsByMeasureYearGradePeriod(
                            interps,
                            measure,
                            null,
                            grade,
                        );
                        const zones = getStudentResultsByMeasureYearGradePeriod(
                            get(results, `${studentId}.growthZones`, []),
                            measure,
                            schoolYear,
                        );
                        return flatMap(toyPeriods, period => {
                            const customGoal = getStudentResultByMeasureYearGradePeriod(
                                get(customGoals, `${studentId}`, []),
                                measure,
                                schoolYear,
                                grade,
                                period,
                            );
                            return {
                                measure,
                                schoolYear,
                                grade,
                                period,
                                goal: getStudentGoal({
                                    measure,
                                    period,
                                    statuses,
                                    interps: interpsByMeasureGrade,
                                    growthZones: zones,
                                    customGoal,
                                    getStrugglingAdvancedGoalForMeasure,
                                }),
                            };
                        });
                    }),
                };
            }, {}),
        }),
        {},
    );

export const getStudentsCompositeGoals = (studentsByGrade, studentsGoals, period) =>
    studentsByGrade.reduce(
        (compositeGoalsByGrade, { grade, students }) => ({
            ...compositeGoalsByGrade,
            ...students.reduce((compositeGoals, student) => {
                const { externalId: studentId } = student;
                const goalsByMeasure = get(studentsGoals, `${studentId}`, [])
                    .filter(
                        goalObject =>
                            goalObject.period === getEndPmPeriod(period) && !isNil(goalObject.goal),
                    )
                    .reduce(
                        (measureScores, { measure, goal }) => ({
                            ...measureScores,
                            [measure]: goal,
                        }),
                        {},
                    );
                const compositeGoal = compositeScoresService.calculateCompositeScoreForStudentGoals(
                    getSupportedGrade(grade),
                    getEndPmPeriod(period),
                    goalsByMeasure,
                );
                return {
                    ...compositeGoals,
                    [studentId]: get(compositeGoal, 'overallStatus.compositeScore'),
                };
            }, {}),
        }),
        {},
    );

export const areTogglesEnabled = (results, selectedPeriod, selectedYear) => {
    const isAtLeastOnePercentileExist = data =>
        data.statuses.some(
            ({ percentile, period, measure, schoolYear }) =>
                period !== PERIOD.SUMMER &&
                !isNil(percentile) &&
                (period === selectedPeriod ||
                    (selectedPeriod === SUMMARY && measure === MEASURE.OVERALL)) &&
                schoolYear === selectedYear &&
                D8_MEASURES.includes(measure),
        );
    const isAtLeastOneLexileExist = data =>
        data.statuses.some(
            ({ measure, period, score, schoolYear }) =>
                measure === LEXILE &&
                !isNil(score) &&
                schoolYear === selectedYear &&
                (selectedPeriod === PERIOD.SUMMARY ? true : period === selectedPeriod),
        );
    return {
        lexile: !isNil(results) && Object.values(results).some(isAtLeastOneLexileExist),
        percentile: !isNil(results) && Object.values(results).some(isAtLeastOnePercentileExist),
    };
};

export const getPerformanceTables = (
    currentPeriod,
    selectedPeriod,
    selectedGrades,
    statuses,
    licensingInfo,
    gradeToMeasureMapper,
    toyPeriods,
) => {
    if (isStandaloneExpress(licensingInfo)) {
        const { red, yellow, green, blue, proficient, below } = getOverallPerformanceExpress(
            currentPeriod,
            selectedPeriod,
            selectedGrades,
            statuses,
            FR_EXPRESS_VIEW.measures,
            gradeToMeasureMapper,
            toyPeriods,
        );
        return [
            {
                skillName: TRO_EXPRESS,
                skillTitle: TRO,
                levelTypeTitle: FR_EXPRESS_VIEW.levelTypeTitle,
                levelTitles: FR_EXPRESS_VIEW.levelTroTitles,
                performanceRows: {
                    [RED]: red,
                    [YELLOW]: yellow,
                    [GREEN]: green,
                    [BLUE]: blue,
                },
            },
            {
                skillName: DO_EXPRESS,
                skillTitle: DO,
                levelTypeTitle: FR_EXPRESS_VIEW.levelTypeTitle,
                levelTitles: FR_EXPRESS_VIEW.levelDoTitles,
                performanceRows: {
                    [BELOW]: below,
                    [PROFICIENT]: proficient,
                },
            },
        ];
    }
    const performance = getOverallPerformance(
        currentPeriod,
        selectedPeriod,
        selectedGrades,
        statuses,
        FR_READING_VIEW.measures,
        getAvailableMeasuresForView,
        gradeToMeasureMapper,
        toyPeriods,
    );
    return [
        {
            key: FR_READING_VIEW.license,
            levelTypeTitle: FR_READING_VIEW.levelTypeTitle,
            levelTitles: FR_READING_VIEW.levelTitles,
            skills:
                selectedPeriod === PERIOD.SUMMARY
                    ? null
                    : getAvailableMeasuresForView(
                          selectedGrades,
                          FR_READING_VIEW.measures,
                          gradeToMeasureMapper,
                      ),

            performanceRows: {
                [RED]: performance.red,
                [YELLOW]: performance.yellow,
                [GREEN]: performance.green,
                [BLUE]: performance.blue,
            },
        },
    ];
};

export const getResultsTableMeasures = (
    selectedPeriod,
    gradesInCurrentClass,
    licensingInfo,
    gradeToMeasureMapper,
    toyPeriods,
) => {
    const measures =
        selectedPeriod === PERIOD.SUMMARY
            ? getMeasurePeriodCombinationForGrades(
                  gradeToMeasureMapper,
                  MEASURES,
                  SUMMARY_MEASURES,
                  toyPeriods,
                  gradesInCurrentClass,
              )
            : getUniqueMeasuresForGrades(gradeToMeasureMapper, MEASURES, gradesInCurrentClass);
    return getLicensedMeasures(measures, licensingInfo);
};

export const getResultsTableSummaryMeasures = (
    gradesInCurrentClass,
    licensingInfo,
    gradeToMeasureMapper,
) => {
    const measures = getSummaryMeasuresForGrades(
        gradeToMeasureMapper,
        MEASURES,
        SUMMARY_MEASURES,
        gradesInCurrentClass,
    );
    return getLicensedMeasures(measures, licensingInfo);
};

export const getStudentSummaryTableMeasures = (gradeYears, historicalGradeToMeasuresMapper) =>
    getUniqueMeasuresForGrades(
        historicalGradeToMeasuresMapper,
        MEASURES,
        gradeYears.map(gradeYear => gradeYear.grade),
    );

export const getBenchmarkHistoryData = (
    interps,
    gradeYears,
    measures,
    statuses,
    historicalGradeToMeasuresMapper,
    allAvailableMeasuresForGrades,
    toyPeriods,
    summerPeriodLabel,
    isLouisianaUser = false,
) => {
    const headerObjects = getHeaderObjects(
        gradeYears,
        toyPeriods,
        summerPeriodLabel,
        isLouisianaUser,
    );

    const resultsObjects = getResultsObjects({
        measures,
        gradeYears,
        interps,
        statuses,
        historicalGradeToMeasuresMapper,
        getGoalByMeasureGradePeriod,
        startedMeasures: MEASURES.filter(
            ({ key }) => key !== LEXILE && allAvailableMeasuresForGrades.includes(key),
        ),
        toyPeriods,
        getSpecificGoal: getSpecificReadingGoal,
        isLouisianaUser,
    });

    return {
        rowsObjects: [...headerObjects.rowsObjects, ...resultsObjects.rowsObjects],
        colSpansObjects: [...headerObjects.colSpansObjects, ...resultsObjects.colSpansObjects],
    };
};

export const getIntercomInfo = ({
    location,
    firstName,
    lastName,
    intercomData,
    isIntercomInfoFetched,
    selectedPeriod,
    selectedPmPeriod,
    selectedGaPeriod,
    selectedPeriodWithoutSummary,
    views,
}) => ({
    ...getSharedIntercomInfo(
        location,
        firstName,
        lastName,
        intercomData,
        isIntercomInfoFetched,
        views,
    ),
    mclass_report_subtype: getD8ReportSubtype(location),
    mclass_report_period: getD8ReportPeriod(location, {
        selectedPeriod,
        selectedPmPeriod,
        selectedGaPeriod,
        selectedPeriodWithoutSummary,
    }),
});

export const getStudentsPmDataByGrades = (
    studentsByGrades,
    results,
    interps,
    year,
    pmPeriod,
    windowDates,
    goals,
    toyPeriods,
) =>
    studentsByGrades.map(({ grade, students }) => ({
        grade,
        studentsData: students.map(student => ({
            studentInfo: getStudentInfo(student),
            composite: getStudentOverallObject(
                results,
                student.externalId,
                year,
                getStartPmPeriod(pmPeriod),
                grade,
            ),
            data: getPmGridData(
                getStudentResultsByMeasureYearGradePeriod(
                    getStudentStatusesById(results, student.externalId),
                    null,
                    year,
                    grade,
                ),
                grade,
                pmPeriod,
                windowDates,
                interps,
                getStudentResultsByMeasureYearGradePeriod(
                    get(goals, `${student.externalId}`, []),
                    null,
                    year,
                    grade,
                ),
                toyPeriods,
            ),
        })),
    }));

export const getSummaryInfoData = (
    assessedStudentsReading,
    inProgressStudents,
    assessedStudentsExpress,
    licenseInfo,
    notAssessedStudents,
) =>
    isStandaloneExpress(licenseInfo)
        ? {
              ...FR_SUMMARY_VIEW_EXPRESS,
              assessedStudentsExpress,
          }
        : {
              ...FR_SUMMARY_VIEW,
              assessment: {
                  [FR_SUMMARY_VIEW.status[0]]: assessedStudentsReading,
                  [FR_SUMMARY_VIEW.status[1]]: notAssessedStudents,
                  [FR_SUMMARY_VIEW.status[2]]: inProgressStudents,
              },
          };

export const getNumberOfAssessedStudentsExpress = (
    results,
    studentIdsByGrade,
    year,
    selectedPeriod,
    currentPeriod,
    grade,
) => {
    const period = selectedPeriod === PERIOD.SUMMARY ? currentPeriod : selectedPeriod;
    return FR_EXPRESS_VIEW_MEASURES.reduce((accumulator, currentValue) => {
        const studentsCount = studentIdsByGrade.reduce((number, studentId) => {
            const status = compact([
                getStudentByMeasureYearPeriodGrade(
                    results,
                    studentId,
                    currentValue,
                    year,
                    period,
                    grade,
                ),
            ]);
            return isStudentAssessed(status) ? number + 1 : number;
        }, 0);

        return {
            ...accumulator,
            [currentValue]: studentsCount,
        };
    }, {});
};

export const getStatusesByLicense = (licensingInfo, results, studentId, year, period, grade) => {
    const measure = isStandaloneExpress(licensingInfo) ? TRO : OVERALL;
    return getStudentByMeasureYearPeriodGrade(results, studentId, measure, year, period, grade);
};
export const getNumberOfAssessedStudents = (
    results,
    studentIdsByGrade,
    year,
    selectedPeriod,
    currentPeriod,
    grade,
    licenseInfo,
) => {
    const period = selectedPeriod === PERIOD.SUMMARY ? currentPeriod : selectedPeriod;
    return studentIdsByGrade.reduce((number, studentId) => {
        const statuses = compact([
            getStatusesByLicense(licenseInfo, results, studentId, year, period, grade),
        ]);
        return isStudentAssessed(statuses) ? number + 1 : number;
    }, 0);
};

export const getNumberOfStudentsInProgress = (
    results,
    studentIdsByGrade,
    year,
    selectedPeriod,
    currentPeriod,
    grade,
    licenseInfo,
) => {
    const period = selectedPeriod === PERIOD.SUMMARY ? currentPeriod : selectedPeriod;
    return studentIdsByGrade.reduce((number, studentId) => {
        const statuses = compact([
            getStatusesByLicense(licenseInfo, results, studentId, year, period, grade),
        ]);
        return isStudentInProgress(statuses, licenseInfo) ? number + 1 : number;
    }, 0);
};

export const getStatusesByYearPeriodGradeMeasure = (results, year, period, grade, licenseInfo) =>
    Object.keys(results).reduce(
        (studentsResults, studentId) => [
            ...studentsResults,
            ...results[studentId].statuses.filter(
                status =>
                    status.schoolYear === year &&
                    (period === PERIOD.SUMMARY ? true : status.period === period) &&
                    (grade === ALL_GRADES ? true : status.grade === grade) &&
                    (period === PERIOD.SUMMARY
                        ? isShowOnSummaryViewByMeasure(licenseInfo, status.measure)
                        : true),
            ),
        ],
        [],
    );

export const getAvailablePrintItems = (
    selectedPeriod,
    licensingInfo,
    studentsByGrade,
    gradeToMeasureMapper,
    pdfAvailabilityConfig,
) => {
    if (isEmpty(pdfAvailabilityConfig)) return [];

    const pdfAvailabilityMapper = getPdfAvailabilityMapper(pdfAvailabilityConfig.pdfAvailability);

    return [
        {
            id: CURRENT_PAGE,
            title: getTitlePdf(CURRENT_PAGE, false),
            isAvailable: true,
            isNew: false,
        },
        {
            id: CLASS_SUMMARY_PDF,
            title: getTitlePdf(CLASS_SUMMARY_PDF, pdfAvailabilityMapper[CLASS_SUMMARY_PDF]),
            isAvailable: isAvailableToyPdfByMeasure(
                selectedPeriod,
                licensingInfo,
                studentsByGrade,
                OVERALL,
                LICENSE.D8,
                gradeToMeasureMapper,
            ),
            isNew: pdfAvailabilityMapper[CLASS_SUMMARY_PDF],
        },
        {
            id: ALL_STUDENT_SUMMARY_PDF,
            title: getTitlePdf(
                ALL_STUDENT_SUMMARY_PDF,
                pdfAvailabilityMapper[ALL_STUDENT_SUMMARY_PDF],
            ),
            isAvailable: !isStandaloneExpress(licensingInfo),
            isNew: pdfAvailabilityMapper[ALL_STUDENT_SUMMARY_PDF],
        },
        {
            id: ALL_PM_GRAPHS_PDF,
            title: getTitlePdf(ALL_PM_GRAPHS_PDF, pdfAvailabilityMapper[ALL_PM_GRAPHS_PDF]),
            isAvailable: !isStandaloneExpress(licensingInfo),
            isNew: pdfAvailabilityMapper[ALL_PM_GRAPHS_PDF],
        },
        {
            id: DUAL_LANGUAGE_CLASS_SUMMARY_PDF,
            title: getTitlePdf(
                DUAL_LANGUAGE_CLASS_SUMMARY_PDF,
                pdfAvailabilityMapper[DUAL_LANGUAGE_CLASS_SUMMARY_PDF],
            ),
            isAvailable: selectedPeriod !== PERIOD.SUMMARY && isDualLanguageEnabled(licensingInfo),
            isNew: pdfAvailabilityMapper[DUAL_LANGUAGE_CLASS_SUMMARY_PDF],
        },
        {
            id: TRO_CLASS_SUMMARY_PDF,
            title: getTitlePdf(TRO_CLASS_SUMMARY_PDF, pdfAvailabilityMapper[TRO_CLASS_SUMMARY_PDF]),
            isAvailable:
                selectedPeriod !== PERIOD.SUMMER &&
                isAvailableToyPdfByMeasure(
                    selectedPeriod,
                    licensingInfo,
                    studentsByGrade,
                    TRO,
                    EXPRESS,
                    gradeToMeasureMapper,
                ),
            isNew: pdfAvailabilityMapper[TRO_CLASS_SUMMARY_PDF],
        },
    ].filter(({ isAvailable }) => isAvailable);
};

export const getTroClassSummaryPdfData = ({
    isDataReady,
    selectedPeriod,
    licensingInfo,
    studentsByGrade,
    allTroBooksData,
    results,
    year,
    gradeToMeasureMapper,
    officialTeacherInfo: { teacherName, teacherSurname },
}) =>
    isDataReady &&
    !isEmpty(allTroBooksData) &&
    licensingInfo.includes(EXPRESS) && {
        isExpress: isStandaloneExpress(licensingInfo),
        rows: compact(
            studentsByGrade.map(({ grade, students }) => {
                const isTroAvailable = get(
                    Object.values(gradeToMeasureMapper).find(({ key }) => key === grade),
                    'measures',
                    [],
                ).includes(TRO);

                const booksDataByPeriodGrade = get(allTroBooksData, `${selectedPeriod}.${grade}`);

                return (
                    isTroAvailable && {
                        grade,
                        teacher: {
                            firstName: teacherName,
                            lastName: teacherSurname,
                        },
                        goals: booksDataByPeriodGrade?.thresholds || {},
                        additionalPage: booksDataByPeriodGrade?.booksInfo || {},
                        students: students.map(student => {
                            const statuses = getStudentStatusesById(results, student.externalId);
                            return {
                                studentInfo: getStudentInfo(student),
                                status: getStudentResultByMeasureYearGradePeriod(
                                    statuses,
                                    TRO,
                                    year,
                                    null,
                                    selectedPeriod,
                                ),
                            };
                        }),
                    }
                );
            }),
        ),
    };

export const getClassSummaryPdfData = ({
    isDataReady,
    studentsByGrade,
    results,
    interps,
    year,
    selectedPeriod,
    currentPeriod,
    gradeToMeasureMapper,
    summerPeriodLabel,
}) =>
    isDataReady && {
        selectedPeriod: selectedPeriod === PERIOD.SUMMER ? summerPeriodLabel : selectedPeriod,
        rows: studentsByGrade.map(({ grade, students }) => {
            const availableMeasures = sortByPredefinedOrder(
                get(
                    Object.values(gradeToMeasureMapper).find(({ key }) => key === grade),
                    'measures',
                    [],
                ).filter(
                    measure =>
                        ![LEXILE, TRO, DO].includes(measure) &&
                        hasMeasureGradeNoExceptions(measure, grade, selectedPeriod),
                ),
                ALL_MEASURES,
            );
            const additionalMeasuresStartIndex = availableMeasures.findIndex(measure =>
                [...TRC_OL_MEASURES, VOCABO, SPELLING, RAN].some(
                    additionalMeasure => measure === additionalMeasure,
                ),
            );
            const availableMeasuresGoals = availableMeasures.map(measure => ({
                measure,
                goal: getGoalReading(true, interps, measure, grade, selectedPeriod, ''),
            }));
            return {
                grade,
                availableMeasuresGoals,
                additionalMeasuresStartIndex,
                students: students.map(student => {
                    const statuses = getStudentStatusesById(results, student.externalId);
                    return {
                        studentInfo: getStudentInfo(student),
                        isDyslexiaRiskDisplayed: isDyslexiaRiskDisplayed(
                            statuses,
                            year,
                            selectedPeriod,
                            currentPeriod,
                        ),
                        isStudentHasUnofficialResults: isStudentHasUnofficialResults(
                            statuses,
                            year,
                            selectedPeriod,
                        ),
                        results: getClassSummaryColumns(
                            availableMeasures,
                            selectedPeriod,
                            statuses,
                            year,
                        ),
                    };
                }),
            };
        }),
    };
